/**
 * Solution detail — Journey / Steps scroll interaction (AW-34).
 *
 * As the visitor scrolls the journey section, the step crossing the middle of
 * the viewport becomes "active": the counter advances (01 → 02 …), the centre
 * progress line fills red down to a square marker at the active step, the
 * sticky image crossfades to that step's image, and inactive steps dim.
 * Pure IntersectionObserver — no scroll-listener thrash.
 *
 * Markup hooks (views/solutions/single-solutions.twig):
 *   [data-journey]            section root
 *   [data-journey-grid]       the two-column container (position reference)
 *   [data-journey-step="i"]   each step (0-based)
 *   [data-journey-counter]    the "01" counter
 *   [data-journey-fill]       red fill inside the centre track
 *   [data-journey-progress]   the square marker on the centre track
 *   [data-journey-media="i"]  stacked per-step images (crossfaded)
 */
// Active instances' [root, teardown] pairs — torn down on htmx:before-swap so
// boost navigation doesn't leak observers / window listeners onto detached
// nodes, AND so the root is dropped from wiredRoots (idiomorph can hand the
// same node back to a structurally-identical destination section, which must
// be re-initialised rather than treated as still-wired).
const journeyTeardowns: Array<[HTMLElement, () => void]> = [];

// In-memory guard: tracks roots wired since their last teardown. WeakSet
// survives history-restore correctly — restored DOM nodes are new objects so
// they pass the has() check even if the cached HTML carried a stale
// data-attribute flag.
const wiredRoots = new WeakSet<HTMLElement>();

function initJourney(root: HTMLElement): void {
  const steps = Array.from(
    root.querySelectorAll<HTMLElement>("[data-journey-step]"),
  );
  if (!steps.length) return;

  const grid = root.querySelector<HTMLElement>("[data-journey-grid]") ?? root;
  const fill = root.querySelector<HTMLElement>("[data-journey-fill]");
  const marker = root.querySelector<HTMLElement>("[data-journey-progress]");
  const medias = Array.from(
    root.querySelectorAll<HTMLElement>("[data-journey-media]"),
  );

  let active = -1;

  const setActive = (index: number): void => {
    if (index === active) return;
    active = index;

    steps.forEach((step, i) => {
      step.style.opacity = i === index ? "1" : "0.3";
    });

    medias.forEach((m) => {
      m.style.opacity = Number(m.dataset.journeyMedia) === index ? "1" : "0";
    });
  };

  setActive(0);


  // Scroll-driven line: dot is fixed at 50% viewport height, line fills
  // from the grid top down to that anchor. Clamped so it starts at 0 when
  // the section enters and stops at full height when it exits.
  const updateLine = (): void => {
    const g = grid.getBoundingClientRect();
    const anchorY = window.innerHeight * 0.5 - g.top;
    const clampedY = Math.max(0, Math.min(g.height, anchorY));
    if (fill) fill.style.height = `${clampedY}px`;
    if (marker) marker.style.top = `${clampedY}px`;
  };

  updateLine();

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .map((e) => steps.indexOf(e.target as HTMLElement))
        .filter((i) => i !== -1);
      if (visible.length) setActive(Math.max(...visible));
    },
    { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
  );
  steps.forEach((step) => observer.observe(step));

  let raf = 0;
  const onScroll = (): void => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(updateLine);
  };
  const reflow = (): void => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const current = active;
      active = -1;
      setActive(current < 0 ? 0 : current);
      updateLine();
    });
  };

  // Lenis drives a smoothed scroll position in its own rAF tick and emits
  // "scroll" synchronously within that tick (before paint). Updating the
  // marker there keeps it in the same frame as the scroll — a native
  // "scroll" + rAF lands one frame late, so the dot visibly lags on fast
  // scroll. Fall back to the native listener only when Lenis is absent.
  const lenis = (window as unknown as { lenis?: { on?: Function; off?: Function } }).lenis;
  const useLenis = !!(lenis && typeof lenis.on === "function");
  if (useLenis) {
    lenis!.on!("scroll", updateLine);
  } else {
    window.addEventListener("scroll", onScroll, { passive: true });
  }
  window.addEventListener("resize", reflow);
  window.addEventListener("load", reflow);

  journeyTeardowns.push([
    root,
    () => {
      observer.disconnect();
      if (useLenis && typeof lenis!.off === "function") {
        lenis!.off!("scroll", updateLine);
      } else {
        window.removeEventListener("scroll", onScroll);
      }
      window.removeEventListener("resize", reflow);
      window.removeEventListener("load", reflow);
    },
  ]);
}

function initJourneyAll(): void {
  document
    .querySelectorAll<HTMLElement>("[data-journey]")
    .forEach((root) => {
      // Skip roots already wired so a partial htmx:load doesn't double-init.
      if (wiredRoots.has(root)) return;
      wiredRoots.add(root);
      initJourney(root);
    });
}

function destroyJourneyAll(): void {
  journeyTeardowns.splice(0).forEach(([root, fn]) => {
    fn();
    wiredRoots.delete(root);
  });
}

// htmx boost swaps content without firing DOMContentLoaded — re-init on
// htmx:load and tear down on htmx:before-swap (same lifecycle as embla/globe).
// htmx:historyRestore fires on back/forward — htmx:load is NOT fired then,
// so we listen separately to re-attach observers to the restored DOM.
//
// Init NOW as well: this is a per-view bundle (lb_add_script on
// single-solutions), so on the first boosted nav to a solutions page in a
// session the module script is inserted BY the swap and executes only after
// that swap's htmx:load events have all fired (and DOMContentLoaded is long
// past) — listeners alone would leave the journey dead until the next nav.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initJourneyAll);
} else {
  initJourneyAll();
}
document.addEventListener("htmx:load", initJourneyAll);
document.addEventListener("htmx:historyRestore", initJourneyAll);
document.addEventListener("htmx:before-swap", destroyJourneyAll);

/**
 * Keyed Alpine re-init for boosted solution→solution navs (this bundle is
 * only loaded on views/solutions/single-solutions.twig — see the tech-specs
 * tabs it lives next to, and lb_add_script('solution-journey') in that view).
 *
 * The tech-specs tabs (`x-data="{ tab: 0 }"`) apply their active classes at
 * runtime via `:class`. idiomorph preserves DOM node identity for
 * structurally-matching sections across a boosted nav, so the reused
 * `[data-tabs-key]` root keeps its old Alpine scope — `tab` is unchanged, its
 * `:class` effect never re-runs, and no tab button reads as selected on the
 * new solution even though the panel content is (correctly) morphed.
 *
 * Fix: tag the x-data root with `data-tabs-key="{{ post.ID }}"`. On htmx:load,
 * compare each key against the last-wired key recorded in a WeakMap. A node
 * seen for the first time was just Alpine-initialised by Alpine's own mutation
 * observer — leave it alone. A node whose key changed is a reused node with a
 * stale scope — force Alpine to re-init it via destroyTree + initTree.
 */
const tabsKeys = new WeakMap<HTMLElement, string>();

function reinitKeyedAlpineAll(): void {
  const Alpine = (
    window as unknown as {
      Alpine?: {
        destroyTree: (el: Element) => void;
        initTree: (el: Element) => void;
        nextTick: (cb: () => void) => void;
      };
    }
  ).Alpine;

  document.querySelectorAll<HTMLElement>("[data-tabs-key]").forEach((el) => {
    const key = el.dataset.tabsKey ?? "";
    const prev = tabsKeys.get(el);

    if (prev === undefined) {
      // First sighting of this node — Alpine's own mutation observer already
      // initialised it on insertion. Just record the key as the baseline.
      tabsKeys.set(el, key);
      return;
    }
    if (prev === key) return; // same content reused as-is — scope still valid

    tabsKeys.set(el, key);
    if (!Alpine) return;
    Alpine.destroyTree(el);
    // Defer initTree to Alpine's own scheduler tick rather than calling it
    // synchronously right after destroyTree, so any pending DOM reactions
    // from the teardown are settled before the fresh instance binds.
    Alpine.nextTick(() => Alpine.initTree(el));
  });
}

// Record baselines at module execution too (not just on later htmx:load):
// executing late — mid-first-boosted-visit — must still baseline the current
// page's keyed nodes, or the NEXT nav's reused nodes read as "first sighting"
// and keep their stale Alpine scope.
if (document.readyState !== "loading") reinitKeyedAlpineAll();
document.addEventListener("htmx:load", reinitKeyedAlpineAll);
